import { useMemo } from 'react'
import { Link, useLocation } from 'react-router'
import { z } from 'zod'
import { TruncatedText } from '@/components/commons/truncated-text'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { DataTable } from '@/components/ui/data-table'
import { EyeIcon } from '@/components/ui/icons'
import { Switch } from '@/components/ui/switch'
import { formatDate } from '@/dates'
import { EmptyStateListings } from '@/features/luxe-submissions/components/empty-state-listings'
import { FilterLocationSelect } from '@/features/luxe-submissions/components/filter-location-select'
import { SourceBadge } from '@/features/luxe-submissions/components/source-badge'
import { SourceFilterSelect } from '@/features/luxe-submissions/components/source-filter-select'
import { SubmissionTypeFilterSelect } from '@/features/luxe-submissions/components/submission-type-filter-select'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import { useGetQueueListings } from '@/features/luxe-submissions/queries/use-get-queue-listings'
import {
	type QueueListing,
	SubmissionSource,
	SubmissionType
} from '@/features/luxe-submissions/types'
import {
	ML_REVIEW_BADGE_STYLE,
	precheckSourceOptions,
	propertyTypeToBadgeStyle,
	propertyTypeToLabelTKey,
	submissionStatusToBadgeStyle,
	submissionStatusToLabelTKey,
	submissionTypeToLabelTKey,
	toArrayParam
} from '@/features/luxe-submissions/utils'
import type { Order, OrderDirection } from '@/helpers/types'
import { cn, getCountryNameFromCode } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { useRowsPerPage } from '@/hooks/use-rows-per-page'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

const InitialEvaluationsSearchParamsSchema = z.object({
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	city: z.string().optional(),
	submissionType: SubmissionType.optional(),
	source: SubmissionSource.optional(),
	showRejected: z.enum(['true']).optional(),
	showOnlyToReview: z.enum(['true']).optional(),
	page: z.string().optional(),
	perPage: z.string().optional()
})

type SortColumn =
	| 'hostEmail'
	| 'propertyAddress'
	| 'country'
	| 'submissionType'
	| 'propertyType'
	| 'precheckStatus'

function LuxeSubmissionsInitialEvaluations() {
	const { t } = useTranslation()
	const location = useLocation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		InitialEvaluationsSearchParamsSchema
	)

	const currentPage = searchParams.page ? Number(searchParams.page) : 1
	const { isEnabled: isPrecheckColumnsEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-precheck-queue-columns'
	)
	const { isEnabled: isMlReviewEnabled } =
		useLuxeFeatureFlag('luxe-vv2-ml-review')
	const { isEnabled: isVendorPrecheckEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-vendor-precheck'
	)
	// Mirrors the backend's `precheckPopulationVisible()`: either flag makes the
	// `precheckState` field come back on the rows and the filter be honoured, so
	// gating the UI on ml-review alone would hide a working feature.
	const isPrecheckPopulationVisible =
		isMlReviewEnabled || isVendorPrecheckEnabled
	// The submission type only varies once Salesforce submissions are actually
	// written: every other pre-check row is field_team_nal, so the column and
	// its filter stay hidden until the writer is live.
	const { isEnabled: isSalesforceAutocreateEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-sf-submission-autocreate'
	)

	const { rowsPerPage, setRowsPerPage, totalRows, reportTotal } =
		useRowsPerPage({
			searchParam: searchParams.perPage,
			setSearchParam: (newPerPage) =>
				setSearchParams({ perPage: newPerPage, page: undefined })
		})

	// Queries
	const { data: listingsData, isLoading: isLoadingListings } =
		useGetQueueListings({
			page: currentPage,
			perPage: rowsPerPage,
			sortBy: searchParams.sortBy,
			sortOrder: searchParams.sortOrder,
			city: searchParams.city,
			submissionType: searchParams.submissionType,
			sources: toArrayParam(searchParams.source),
			// The standard view is everything, rejected and ML Review included,
			// which is what the endpoint returns with no status filter at all,
			// so the toggle below only ever narrows down to the rejected ones.
			statuses:
				searchParams.showRejected === 'true' ? ['rejected'] : undefined,
			// "Show only to review" narrows the queue to the population still
			// waiting on a vendor/Ops decision, leaving out the ML Review rows.
			precheckState:
				searchParams.showOnlyToReview === 'true'
					? 'pending_review'
					: undefined
		})

	// Pagination
	const lastPage = listingsData ? listingsData.meta.lastPage : undefined
	reportTotal(listingsData?.meta.total)

	const setPage = (page: number) => {
		setSearchParams({
			page: page === 1 ? undefined : String(page)
		})
	}

	// Filters
	const handleLocationChange = (city: string | null) => {
		setSearchParams({
			city: city ?? undefined,
			page: undefined
		})
	}

	const handleSubmissionTypeChange = (submissionType: string | null) => {
		setSearchParams({
			submissionType: (submissionType as SubmissionType) ?? undefined,
			page: undefined
		})
	}

	const handleSourceChange = (source: string | null) => {
		setSearchParams({
			source: (source as SubmissionSource) ?? undefined,
			page: undefined
		})
	}

	// Order state for DataTable
	const order = useMemo((): Order<SortColumn> | undefined => {
		if (!searchParams.sortBy) {
			return undefined
		}
		return {
			orderBy: searchParams.sortBy as SortColumn,
			orderDirection: searchParams.sortOrder as OrderDirection | undefined
		}
	}, [searchParams.sortBy, searchParams.sortOrder])

	const setOrder = (newOrder?: Order<SortColumn>) => {
		setSearchParams({
			sortBy: newOrder?.orderBy,
			sortOrder: newOrder?.orderDirection,
			page: undefined
		})
	}
	// The two toggles carve out disjoint populations — rejected rows are never
	// awaiting review — so turning one on clears the other rather than leaving
	// a combination that can only ever come back empty.
	const handleShowRejectedChange = (checked: boolean) => {
		setSearchParams({
			showRejected: checked ? 'true' : undefined,
			showOnlyToReview: checked
				? undefined
				: searchParams.showOnlyToReview,
			page: undefined
		})
	}

	const handleShowOnlyToReviewChange = (checked: boolean) => {
		setSearchParams({
			showOnlyToReview: checked ? 'true' : undefined,
			showRejected: checked ? undefined : searchParams.showRejected,
			page: undefined
		})
	}

	const hasFilters = Object.values(searchParams).some(Boolean)

	// Table columns
	const columns = useMemo(
		() => [
			{
				id: 'hostEmail' as const,
				header: t('luxe_submissions:host_email_column'),
				cell: ({ dataItem }: { dataItem: QueueListing }) =>
					dataItem.hostEmail ? (
						<span
							className='truncate font-medium text-neutral-600 text-sm'
							title={dataItem.hostEmail}
						>
							{dataItem.hostEmail}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					),
				size: 250,
				sortKey: 'hostEmail' as const
			},
			{
				id: 'propertyAddress' as const,
				header: t('luxe_submissions:property_address_column'),
				cell: ({ dataItem }: { dataItem: QueueListing }) => (
					<div className='flex flex-col'>
						<span className='text-neutral-900 text-sm'>
							{dataItem.listingCity}
						</span>

						{dataItem.propertyAddress ? (
							<TruncatedText className='text-neutral-600 text-sm'>
								{dataItem.propertyAddress}
							</TruncatedText>
						) : (
							<span className='text-neutral-500 text-sm'>—</span>
						)}
					</div>
				),
				size: 250,
				sortKey: 'propertyAddress' as const
			},
			{
				id: 'country' as const,
				header: t('luxe_submissions:location_column'),
				cell: ({ dataItem }: { dataItem: QueueListing }) => {
					const countryName = getCountryNameFromCode(
						dataItem.listingCountryCode
					)
					return countryName ? (
						<span className='text-neutral-600 text-sm'>
							{countryName}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					)
				},
				size: 160,
				sortKey: 'country' as const
			},
			{
				id: 'source' as const,
				header: t('luxe_submissions:source_column'),
				cell: ({ dataItem }: { dataItem: QueueListing }) =>
					dataItem.source ? (
						<SourceBadge source={dataItem.source} />
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					),
				size: 160
			},
			...(isSalesforceAutocreateEnabled
				? [
						{
							id: 'submissionType' as const,
							header: t(
								'luxe_submissions:submission_type_column'
							),
							cell: ({ dataItem }: { dataItem: QueueListing }) =>
								dataItem.submissionType ? (
									<span className='text-neutral-700 text-sm'>
										{t(
											submissionTypeToLabelTKey[
												dataItem.submissionType
											]
										)}
									</span>
								) : (
									<span className='text-neutral-500 text-sm'>
										—
									</span>
								),
							size: 210,
							sortKey: 'submissionType' as const
						}
					]
				: []),
			...(isPrecheckColumnsEnabled
				? [
						{
							id: 'submittedAt' as const,
							header: t('luxe_submissions:submitted_at_column'),
							cell: ({ dataItem }: { dataItem: QueueListing }) =>
								dataItem.queueEnteredAt ||
								dataItem.daysInQueue !== null ? (
									<div className='flex flex-col'>
										<span className='text-neutral-700 text-sm'>
											{formatDate(
												dataItem.queueEnteredAt,
												'PP'
											)}
										</span>
										{dataItem.daysInQueue !== null && (
											<span className='text-neutral-600 text-sm'>
												{t(
													dataItem.daysInQueue === 0
														? 'luxe_submissions:enter_queue_today'
														: 'luxe_submissions:days_in_queue',
													{
														count: dataItem.daysInQueue
													}
												)}
											</span>
										)}
									</div>
								) : (
									<span className='text-neutral-500 text-sm'>
										—
									</span>
								),
							size: 190
						}
					]
				: []),
			{
				id: 'propertyType' as const,
				header: t('luxe_submissions:property_type_column'),
				cell: ({ dataItem }: { dataItem: QueueListing }) => {
					const badgeStyle = dataItem.propertyType
						? propertyTypeToBadgeStyle[dataItem.propertyType]
						: 'bg-neutral-100 border-neutral-300 text-neutral-700'
					return dataItem.propertyType ? (
						<span
							className={cn(
								'inline-flex rounded-full border px-2 py-0.5 font-medium text-xs',
								badgeStyle
							)}
						>
							{t(propertyTypeToLabelTKey[dataItem.propertyType])}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					)
				},
				size: 182,
				sortKey: 'propertyType' as const
			},

			{
				id: 'precheckStatus' as const,
				header: t('luxe_submissions:status_column'),
				cell: ({ dataItem }: { dataItem: QueueListing }) => {
					// A submission waiting on the Salesforce ML verdict shows
					// that population in place of its own status.
					const isAwaitingMlReview =
						isPrecheckPopulationVisible &&
						dataItem.precheckState === 'ml_review'

					return (
						<span
							className={cn(
								'inline-flex rounded-full border px-2 py-0.5 font-medium text-xs',
								isAwaitingMlReview
									? ML_REVIEW_BADGE_STYLE
									: submissionStatusToBadgeStyle[
											dataItem.precheckStatus
										]
							)}
						>
							{t(
								isAwaitingMlReview
									? 'luxe_submissions:precheck_state_llm_review'
									: submissionStatusToLabelTKey[
											dataItem.precheckStatus
										]
							)}
						</span>
					)
				},
				size: 160,
				sortKey: 'precheckStatus' as const
			},
			{
				id: 'action' as const,
				header: '',
				cell: ({ dataItem }: { dataItem: QueueListing }) => {
					// A row awaiting the Salesforce ML verdict is not the
					// reviewer's to act on, so it falls through to the
					// view-only eye rather than offering "Review".
					return dataItem.precheckStatus === 'pending' &&
						dataItem.precheckState !== 'ml_review' ? (
						<Link
							className='font-medium text-neutral-900 text-sm underline'
							state={{ fromSearch: location.search }}
							to={routes.LUXE_SUBMISSIONS_LISTING.handle.fullPath.replace(
								':id',
								dataItem.id
							)}
						>
							{t('luxe_submissions:review')}
						</Link>
					) : (
						<Link
							className='flex cursor-pointer items-center justify-center text-neutral-500 hover:text-neutral-700'
							state={{ fromSearch: location.search }}
							to={routes.LUXE_SUBMISSIONS_LISTING.handle.fullPath.replace(
								':id',
								dataItem.id
							)}
						>
							<EyeIcon size={20} />
						</Link>
					)
				},
				size: 100
			}
		],
		[
			t,
			location.search,
			isPrecheckColumnsEnabled,
			isPrecheckPopulationVisible,
			isSalesforceAutocreateEnabled
		]
	)

	return (
		<div className='flex flex-1 flex-col overflow-hidden px-10'>
			{/* Filters */}
			<div className='flex shrink-0 items-center gap-3 py-6'>
				<FilterLocationSelect
					onChange={handleLocationChange}
					selectedId={searchParams.city ?? null}
				/>
				{isSalesforceAutocreateEnabled && (
					<SubmissionTypeFilterSelect
						onChange={handleSubmissionTypeChange}
						value={searchParams.submissionType ?? null}
					/>
				)}
				<SourceFilterSelect
					onChange={handleSourceChange}
					options={precheckSourceOptions}
					value={searchParams.source ?? null}
				/>
				<div className='flex items-center gap-2'>
					<Switch
						checked={searchParams.showRejected === 'true'}
						onChange={handleShowRejectedChange}
					/>
					<span className='font-medium text-neutral-700 text-sm'>
						{t('luxe_submissions:show_rejected')}
					</span>
				</div>
				{isPrecheckPopulationVisible && (
					<div className='flex items-center gap-2'>
						<Switch
							checked={searchParams.showOnlyToReview === 'true'}
							onChange={handleShowOnlyToReviewChange}
						/>
						<span className='font-medium text-neutral-700 text-sm'>
							{t('luxe_submissions:show_only_to_review')}
						</span>
					</div>
				)}
			</div>

			{/* Table */}
			<div className='flex-1 overflow-hidden'>
				<DataTable
					columns={columns}
					data={listingsData?.data ?? []}
					emptyComponent={
						<EmptyStateListings
							action={
								hasFilters ? null : (
									<ButtonLegacy
										onClick={() => window.location.reload()}
										variant='secondary'
									>
										{t('common:refresh')}
									</ButtonLegacy>
								)
							}
							description={t(
								hasFilters
									? 'common:empty_table_description'
									: 'luxe_submissions:pre_check_no_listings_description'
							)}
							title={t(
								hasFilters
									? 'common:empty_table_title'
									: 'luxe_submissions:pre_check_no_listings'
							)}
						/>
					}
					isLoading={isLoadingListings}
					lastPage={lastPage}
					order={order}
					page={currentPage}
					rowsPerPage={rowsPerPage}
					setOrder={setOrder}
					setPage={setPage}
					setRowsPerPage={setRowsPerPage}
					totalRows={totalRows}
				/>
			</div>
		</div>
	)
}

export { LuxeSubmissionsInitialEvaluations }
